import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PortableText from 'react-portable-text';
import {
  CashIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationIcon,
  LocationMarkerIcon,
  LockClosedIcon,
  OfficeBuildingIcon,
  PaperClipIcon,
  PrinterIcon,
  ShareIcon,
  ShieldCheckIcon,
  UsersIcon,
  CalendarIcon,
} from '@heroicons/react/outline';
import site from '../site.json';
import { feature } from '../utils/features';
import { jobType, prettyDate } from '../utils/dates';
import SaveButton from './SaveButton';

// The refreshed job page (feature "modernJobPage", RefugeeJobLeads first). Same data, apply flow and
// rules as JobView's classic layout; JobView passes in its SafeLink and MailtoLink so links behave the same.
// Printing gives just the job on clean pages: buttons, the apply box and the safety tip are hidden
// (print:hidden), the site header/footer are hidden in App.js, and the jd-* print rules in index.css
// flatten the cards into a plain document.

const serializers = (SafeLink) => ({
  h1: (props) => <h2 className="mb-2 mt-6 text-xl font-bold text-gray-900" {...props} />,
  h2: (props) => <h2 className="mb-2 mt-6 text-xl font-bold text-gray-900" {...props} />,
  h3: (props) => <h3 className="mb-2 mt-5 text-lg font-bold text-gray-900" {...props} />,
  h4: (props) => <h4 className="mb-2 mt-4 font-semibold text-gray-900" {...props} />,
  li: (props) => <li className="my-1.5 ml-5 list-disc" {...props} />,
  link: SafeLink,
  normal: (props) => <p className="my-3 leading-relaxed" {...props} />,
});

const Fact = ({ icon: Icon, label, children }) => (
  <div className="jd-fact flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3">
    <Icon className="h-5 w-5 flex-shrink-0 text-primary-dark" aria-hidden="true" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="truncate text-sm font-semibold text-gray-900">{children || '—'}</p>
    </div>
  </div>
);

function ShareButtons({ title }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href.split('#')[0] : '';
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${title} on ${site.name}`, url });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Clipboard blocked: nothing to do; the address bar still has the link.
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={share}
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:border-primary hover:text-primary-dark"
      >
        <ShareIcon className="h-5 w-5" aria-hidden="true" />
        {copied ? 'Link copied' : 'Share'}
      </button>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${title}: ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:border-green-600 hover:text-green-700"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
          <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4c1.7.7 2.4.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z" />
        </svg>
        WhatsApp
      </a>
    </>
  );
}

export default function JobDetailModern({
  btnId,
  jobData,
  displayTitle,
  isPostExpired,
  closedOn,
  applyCfg,
  appliedAt,
  onApply,
  onBack,
  SafeLink,
  MailtoLink,
}) {
  const jobId = `${site.jobIdPrefix}-${(jobData._id || '').slice(0, 4)}${(jobData._id || '').slice(32)}`;
  const content = serializers(SafeLink);

  const applyButton = (big) => {
    const cls = `inline-flex items-center justify-center gap-2 rounded-full font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
      big ? 'w-full px-6 py-3 text-base' : 'px-5 py-2 text-sm'
    }`;
    if (isPostExpired) {
      return (
        <button type="button" disabled aria-disabled="true" className={`${cls} cursor-not-allowed bg-gray-200 text-gray-500`}>
          <LockClosedIcon className="h-5 w-5" aria-hidden="true" />
          Applications closed
        </button>
      );
    }
    if (applyCfg) {
      return (
        <button type="button" onClick={onApply} className={`${cls} bg-primary text-white shadow-sm hover:opacity-90`}>
          <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
          {appliedAt ? 'Applied' : 'Apply now'}
        </button>
      );
    }
    return (
      <MailtoLink
        email={jobData.email}
        subject={`Application for ${displayTitle}`}
        body="Please find attached my resume and cover letter to apply."
        className={big ? 'block' : ''}
        disabledClassName="opacity-50 pointer-events-none"
      >
        <span className={`${cls} bg-primary text-white shadow-sm hover:opacity-90`}>
          <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
          Apply now
        </span>
      </MailtoLink>
    );
  };

  return (
    <div className="jd bg-gray-50">
      <div className="jd-wrap mx-auto max-w-6xl px-4 py-8">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-primary-dark print:hidden">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          Back to jobs
        </button>
        <p className="jd-printline hidden">
          {site.name} · {site.siteUrl.replace(/^https?:\/\//, '')}/jobs/{btnId}
        </p>

        {isPostExpired && (
          <div role="status" className="mt-6 flex items-start gap-3 rounded-2xl border border-yellow-300 bg-yellow-50 p-4">
            <ExclamationIcon className="h-6 w-6 flex-shrink-0 text-yellow-600" aria-hidden="true" />
            <div className="text-sm text-gray-800">
              <p className="font-semibold">This job has expired</p>
              <p className="mt-1">
                The employer stopped accepting applications{closedOn ? ` on ${closedOn}` : ''}.{' '}
                <Link to="/jobs" className="font-semibold text-primary-dark hover:underline print:hidden">
                  Browse other jobs
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Title card */}
        <header className="jd-card jd-head mt-6 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${isPostExpired ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-800'}`}>
              {isPostExpired ? 'Closed' : 'Hiring now'}
            </span>
            <span className="text-xs text-gray-500">Job ID {jobId}</span>
          </div>
          <h1 className="jd-title mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-4xl">{displayTitle}</h1>
          {jobData.companyName && (
            <p className="mt-2 flex items-center gap-2 text-base font-medium text-gray-700">
              <OfficeBuildingIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              {jobData.companyName}
            </p>
          )}
          <div className="jd-facts mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Fact icon={LocationMarkerIcon} label="Location">{jobData.location || jobData.city}</Fact>
            <Fact icon={ClockIcon} label="Job type">{jobType(jobData.categories)}</Fact>
            <Fact icon={CashIcon} label="Pay">{String(jobData.salary || '').trim()}</Fact>
            <Fact icon={UsersIcon} label="Openings">{jobData.vacancies}</Fact>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2 print:hidden">
            {applyButton(false)}
            {feature('savedJobs') && (
              <SaveButton
                withLabel
                job={{ id: btnId, title: displayTitle, companyName: jobData.companyName, categories: jobData.categories, city: jobData.city, salary: jobData.salary, postedOn: jobData.postedOn, postExpiry: jobData.postExpiry }}
              />
            )}
            {feature('shareJob') && <ShareButtons title={displayTitle} />}
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:border-primary hover:text-primary-dark"
            >
              <PrinterIcon className="h-5 w-5" aria-hidden="true" />
              Print
            </button>
          </div>
        </header>

        <div className="jd-grid mt-6 grid gap-6 lg:grid-cols-3">
          {/* Description */}
          <article className="jd-card jd-body rounded-3xl bg-white p-6 text-gray-700 shadow-sm ring-1 ring-gray-200 sm:p-8 lg:col-span-2">
            <h2 className="font-display text-xl font-bold text-gray-900">About the job</h2>
            <PortableText content={jobData.body} projectId={process.env.REACT_APP_SANITY_PROJECT_ID} dataset={process.env.REACT_APP_SANITY_DATASET} serializers={content} />

            <h2 className="mt-8 font-display text-xl font-bold text-gray-900">Job details</h2>
            <dl className="jd-details mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              {[
                ['Status', isPostExpired ? 'Expired' : 'Active'],
                ['Job ID', jobId],
                ['Posted on', prettyDate(jobData.postedOn)],
                ['Closes on', prettyDate(jobData.postExpiry)],
                ['Eligibility', site.eligibility],
                ['Location', jobData.location || jobData.city],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-gray-100 pb-2">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="font-medium text-gray-900">{v || '—'}</dd>
                </div>
              ))}
            </dl>
          </article>

          {/* Sidebar */}
          <aside className="jd-aside space-y-6">
            <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 print:hidden lg:sticky lg:top-6">
              <p className="font-display text-lg font-bold text-gray-900">{isPostExpired ? 'This job is closed' : 'Interested?'}</p>
              <p className="mt-1 text-sm text-gray-600">
                {isPostExpired
                  ? 'The employer is no longer accepting applications.'
                  : `Applications go straight to the employer. Closes ${prettyDate(jobData.postExpiry) || 'soon'}.`}
              </p>
              <div className="mt-4">{applyButton(true)}</div>
            </div>

            <div className="jd-card jd-employer rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Posted by</p>
              <p className="mt-2 font-display text-lg font-bold text-gray-900">{jobData.companyName}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                Posted {prettyDate(jobData.postedOn)}
              </p>
              {Array.isArray(jobData.companyBio) && jobData.companyBio.length > 0 && (
                <div className="mt-4 text-sm text-gray-700">
                  <PortableText content={jobData.companyBio} projectId={process.env.REACT_APP_SANITY_PROJECT_ID} dataset={process.env.REACT_APP_SANITY_DATASET} serializers={content} />
                </div>
              )}
            </div>

            <div className="rounded-3xl bg-primary-light p-6 print:hidden">
              <p className="flex items-center gap-2 font-semibold text-gray-900">
                <ShieldCheckIcon className="h-5 w-5 text-primary-dark" aria-hidden="true" />
                Stay safe
              </p>
              <p className="mt-2 text-sm text-gray-700">
                Real employers never ask you to pay to apply, buy equipment or training, or share banking details before a job offer. If
                something feels wrong, email enquiries@canadajobleads.ca.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
